/**
 * The release plan, as data.
 *
 * <p>It lived inside EsHomeRoadmap until 2026-09-16, which was right while that
 * page was the only thing that knew the schedule. The welcome page needs it too
 * -- it tells a reader when the next release that reaches THEIR tier lands --
 * and a second copy of eighteen dates is a second copy to keep right. One of
 * them would have been wrong within the week, and it would have been the one
 * shown to the people most likely to notice.
 *
 * <p>The comment below is the original, unchanged: it is the derivation, and
 * the derivation is the reason to trust the numbers.
 */

// THREE LEVELS: a phase, the betas in it, and the weekly releases in each
// beta. Every number below is derived from docs/escapade/history.md and
// from this page's own previous contents; nothing here is invented.
//
// THE SCHEDULE IS WEEKLY. history.md's Phase Three names four milestones --
// es-0.5.0 on Sep 3 as the "first Thursday night weekly release", then
// Oct 8, Nov 19 and Dec 31 -- and one release per Thursday from 0.5 makes
// those the 5th, 11th and 17th, so 0.10, 0.16 and 0.22. All three land
// exactly, which is what proves the cadence rather than merely suggests it.
// Eighteen releases, Sep 3 to Dec 31, and the minor number IS the week.
//
// A BETA IS EVERY THIRD WEEK, AND IT IS THAT WEEK'S RELEASE. Not a release
// of its own and not a separate number: 1.0 IS Beta 3. That is why a beta
// carries no date or version of its own below -- it takes the last release
// in its group, and the two cannot drift because there is only one of them.
// Six betas over eighteen weeks, three to a phase.
//
// WHICH MAKES THE OLD SIX ROWS THE SIX BETAS. This page used to show six
// things dated Sep 17, Oct 8, Oct 29, Nov 19, Dec 10 and Dec 31, and those
// are the beta dates below: every third Thursday, bar the Halloween release,
// which is Saturday Oct 31. It had them numbered
// 0.4 to 0.8 and then "1.0", which is the part that was wrong.
//
// NOTE FOR history.md: it labels 0.10/1.3/2.0 as beta1/beta2/beta3. Under
// the every-three-weeks rule those are Beta 2 and the Virtual Phase's Betas 1 and 3. The doc
// is the authority on dates and versions and they agree; only the beta
// NUMBERING disagrees, and that is worth reconciling there rather than here.
//
// WHAT SHIPS EACH WEEK: two themes and then a playtesting week, which is
// what a three-week beta is made of. Twelve pieces over six betas is
// exactly two apiece, so the grid fills with nothing left over and nothing
// invented -- the pairs below are the twelve this page already carried, in
// the order it had them.
//
// PLAYTESTING IS THE THIRD WEEK, every time. It is the week the beta goes
// out and gets played, so it belongs to the beta rather than to a feature,
// and it is the reason a beta is three weeks and not two.
//
// NOTE ON 0.5 AND 0.6: the plan below says Codex then Characters, and the
// release notes say es-0.5.0 shipped BOTH on Sep 3 and es-0.6.0 shipped
// Campaigns and Console on Sep 10 -- so Console arrived a beta early and
// Campaigns two. Reality is ahead of this grid by roughly a beta. The grid
// is left as the plan because that is what a roadmap is; the changelog is
// where what happened is recorded.
// THE LAUNCH IS 1.0 (Tim, 2026-10-31): the Halloween release is 1.0, and the weekly releases
// after it count 1.1 to 1.8, and New Year's is 2.0. Each phase counts its betas from 1, so the Virtual
// Phase's read Beta 1 to 3; `key` (2-1 to 2-3: phase, then beta) keeps them apart from the Tabletop Phase's in test ids.
export const PHASE_LIST = [
    { name: 'Tabletop Phase \u2014 Halloween Release', date: 'Oct 31', betas: [
        { name: 'Beta 1', tone: 'magenta', releases: [
            { version: '0.5', date: 'Sep 3', items: 'Codex' },
            { version: '0.6', date: 'Sep 10', items: 'Characters' },
            { version: '0.7', date: 'Sep 17', items: 'Playtesting' } ] },
        { name: 'Beta 2', tone: 'red', releases: [
            { version: '0.8', date: 'Sep 24', items: 'Console' },
            { version: '0.9', date: 'Oct 1', items: 'Tabletop' },
            { version: '0.10', date: 'Oct 8', items: 'Playtesting' } ] },
        { name: 'Beta 3', tone: 'green', releases: [
            { version: '0.11', date: 'Oct 15', items: 'Campaign Builder' },
            { version: '0.12', date: 'Oct 22', items: 'Homebrew Engine' },
            { version: '1.0', date: 'Oct 31', items: 'Playtesting' } ] } ] },
    { name: 'Virtual Phase \u2014 New Year\u2019s Release', date: 'Dec 31', betas: [
        { name: 'Beta 1', key: '2-1', tone: 'cyan', releases: [
            { version: '1.1', date: 'Nov 5', items: 'Room' },
            { version: '1.2', date: 'Nov 12', items: 'Multiplayer' },
            { version: '1.3', date: 'Nov 19', items: 'Playtesting' } ] },
        { name: 'Beta 2', key: '2-2', tone: 'purple', releases: [
            { version: '1.4', date: 'Nov 26', items: 'Map' },
            { version: '1.5', date: 'Dec 3', items: 'Workbench' },
            { version: '1.6', date: 'Dec 10', items: 'Playtesting' } ] },
        { name: 'Beta 3', key: '2-3', tone: 'sky', releases: [
            { version: '1.7', date: 'Dec 17', items: 'IDE' },
            { version: '1.8', date: 'Dec 24', items: 'Effects' },
            { version: '2.0', date: 'Dec 31', items: 'Playtesting' } ] } ] }
];

/**
 * The year every date above belongs to.
 *
 * <p>The plan is written "Sep 3", "Dec 31" -- the way a roadmap reads -- and a
 * label cannot be compared to today. This is the one place that says which year
 * those labels are in, so the comparison below has something to compare.
 *
 * <p>⚠️ It is 2026 because the plan runs Sep 3 to Dec 31 of 2026. When the plan
 * crosses a new year, this stops being one number and the dates have to carry
 * their own year -- which is the moment to change the shape rather than to bump
 * this and hope.
 */
export const PLAN_YEAR = 2026;

const MONTHS = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
                 Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };

/**
 * One "Sep 3" as a Date, at the END of that day.
 *
 * <p>End of day rather than midnight, so a release dated today still counts as
 * upcoming for the whole of the day it ships on. A reader looking at this page
 * on release morning should be told about today, not about next week.
 *
 * @param pLabel a date as the plan writes it
 * @return a Date, or null if the label is not one
 */
export function planDate( pLabel ) {
    const aParts = String( pLabel || '' ).trim().split( /\s+/ );
    const aMonth = MONTHS[ aParts[ 0 ] ];
    const aDay = Number( aParts[ 1 ] );

    if ( aMonth === undefined || !Number.isFinite( aDay ) ) return null;

    return new Date( PLAN_YEAR, aMonth, aDay, 23, 59, 59, 999 );
}

/** Every release in the plan, flattened, in order. */
export function releaseList() {
    return PHASE_LIST.flatMap( ( pPhase ) =>
        pPhase.betas.flatMap( ( pBeta ) =>
            pBeta.releases.map( ( pRelease ) => ( {
                ...pRelease,
                beta: pBeta.name,
                // A BETA IS THAT WEEK'S RELEASE -- the last one in its group --
                // which is the rule the comment above states and the reason a
                // beta carries no date of its own.
                isBeta: pRelease === pBeta.releases[ pBeta.releases.length - 1 ],
            } ) ) ) );
}

/**
 * The next release on or after a moment, or null past the end of the plan.
 *
 * @param pWhen   the moment to look forward from
 * @param pBetaOnly true to skip to the next BETA release
 */
export function nextRelease( pWhen, pBetaOnly = false ) {
    return releaseList().find( ( pRelease ) => {
        if ( pBetaOnly && !pRelease.isBeta ) return false;

        const aDate = planDate( pRelease.date );
        return aDate && aDate >= pWhen;
    } ) ?? null;
}

/**
 * The release a named feature is planned for, or null if the plan does not
 * name it.
 *
 * <p>Matched on the {@code items} string, which is the label the roadmap grid
 * already prints -- "Codex", "Characters", "Console". A lookup table beside it
 * would be a second list of the same twelve names, and the two would disagree
 * the first time one of them was renamed.
 *
 * <p>Null is a real answer and callers must handle it: a feature that is shut
 * for a reason the plan says nothing about -- a flag off, a rule that does not
 * name them -- has no planned release, and inventing one would be the exact
 * thing EsWelcome's warning block refuses to do.
 *
 * @param pLabel a feature's label, as the plan spells it
 */
export function plannedRelease( pLabel ) {
    const aLabel = String( pLabel || '' ).trim();

    return aLabel
        ? releaseList().find( ( pRelease ) => pRelease.items === aLabel ) ?? null
        : null;
}

/**
 * When a feature reaches a READER, which is not when it ships.
 *
 * <p>Two dates decide it and the later one wins. A feature cannot arrive before
 * the plan builds it, and it cannot arrive before the reader's next release --
 * so Console, planned for 0.8 on Sep 24, reaches a weekly reader on Sep 24 and
 * a beta reader on Oct 8, which is the beta that contains it. Codex, planned in
 * a release that has already gone out, reaches whoever is still waiting on
 * their own next one.
 *
 * <p>That is the whole of the rule, and it is why this is derived rather than
 * listed: nothing here has to be revisited when the plan moves.
 *
 * @param pLabel    a feature's label, as the plan spells it
 * @param pWhen     the moment to look forward from
 * @param pBetaOnly true if this reader is served at beta boundaries
 * @return the release it arrives in, or null past the end of the plan
 */
export function arrivalRelease( pLabel, pWhen, pBetaOnly = false ) {
    const aPlanned = plannedRelease( pLabel );
    const aPlannedDate = aPlanned ? planDate( aPlanned.date ) : null;

    // The later of the two. An unplanned feature falls back to the reader's
    // cadence alone, which is what this page said about everything before.
    const aFrom = aPlannedDate && aPlannedDate > pWhen ? aPlannedDate : pWhen;

    return nextRelease( aFrom, pBetaOnly );
}
